import * as i0 from '@angular/core';
import { Injectable, Component, InjectionToken, ChangeDetectionStrategy, Optional, SkipSelf, Inject, Input, HostBinding, Directive, TemplateRef, ContentChild, NgModule } from '@angular/core';
import * as i4 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i1 from '@angular/router';
import { NavigationStart, NavigationEnd, RouterModule } from '@angular/router';
import * as i2 from '@angular/material/dialog';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { getPathToRouteParent } from '@firestitch/core';
import { BehaviorSubject, Subject, merge } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';
import * as i2$1 from '@angular/cdk/layout';
import * as i1$1 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import * as i2$2 from '@angular/material/icon';
import { MatIconModule } from '@angular/material/icon';

class FsDialogRouter {
    constructor(_router, _dialog) {
        this._router = _router;
        this._dialog = _dialog;
        this._activeDialogs = new WeakMap();
        this._dialogOpened$ = new BehaviorSubject(null);
    }
    openDialogForRoute(component, config) {
        const dialog = this.openDialog(component, config);
        this._registerDialog(component, dialog);
        return dialog;
    }
    navigate(commands, extras) {
        return this._router.navigate(commands, extras);
    }
    openDialog(component, config) {
        const dialog = this._dialog.open(component, config);
        this._registerDialog(component, dialog);
        return dialog;
    }
    dialogRef$(component) {
        return this._dialogOpened$
            .pipe(filter(() => {
            return this._activeDialogs.has(component);
        }), map(() => this._activeDialogs.get(component)));
    }
    _registerDialog(component, ref) {
        if (!this._activeDialogs.has(component)) {
            this._activeDialogs.set(component, ref);
            this._listenDialogClose(component, ref);
            this._dialogOpened$.next();
        }
    }
    _listenDialogClose(component, ref) {
        ref.afterClosed()
            .pipe(take(1))
            .subscribe(() => {
            this._activeDialogs.delete(component);
        });
    }
}
FsDialogRouter.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogRouter, deps: [{ token: i1.Router }, { token: i2.MatDialog }], target: i0.ɵɵFactoryTarget.Injectable });
FsDialogRouter.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogRouter, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogRouter, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.Router }, { type: i2.MatDialog }]; } });

class FsDialogRouteComponent {
    constructor(_route, _router, _dialogRouter, _componentFactory, _viewContainerRef) {
        this._route = _route;
        this._router = _router;
        this._dialogRouter = _dialogRouter;
        this._componentFactory = _componentFactory;
        this._viewContainerRef = _viewContainerRef;
        this._hasActiveNavigation = false;
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        this._listenNavigationEvents();
        this.openDialog();
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
        this._dialog.close();
    }
    async openDialog() {
        const dialogConfig = { ...this._route.snapshot.data?.fsDialog };
        if (dialogConfig?.component) {
            if (dialogConfig.component instanceof Promise) {
                this._dialog = await this._openLazyDialog(dialogConfig);
            }
            else {
                this._dialog = this._openDialog(dialogConfig);
            }
            this._listenDialogClose();
        }
    }
    _listenNavigationEvents() {
        this._router.events
            .pipe(filter((event) => {
            return event instanceof NavigationStart;
        }), takeUntil(this._destroy$))
            .subscribe((event) => {
            this._hasActiveNavigation = true;
        });
        this._router.events
            .pipe(filter((event) => {
            return event instanceof NavigationEnd;
        }), takeUntil(this._destroy$))
            .subscribe((event) => {
            this._hasActiveNavigation = false;
        });
    }
    _listenDialogClose() {
        merge(this._dialog.afterClosed())
            .pipe(filter(() => !this._hasActiveNavigation), takeUntil(this._destroy$))
            .subscribe(() => {
            this._navigateOutFromDialog();
        });
    }
    _navigateOutFromDialog() {
        const navigationPath = getPathToRouteParent(this._route);
        // Do it!
        this._router.navigate([navigationPath], { relativeTo: this._route, queryParamsHandling: 'merge' });
    }
    async _openLazyDialog(dialogConfig) {
        const loadedComponent = await dialogConfig.component;
        const componentName = Object.keys(loadedComponent)[0];
        if (!componentName) {
            throw Error('Lazy loading dialog component error! Component not found!');
        }
        const factory = this._componentFactory.resolveComponentFactory(loadedComponent[componentName]);
        dialogConfig.component = factory.componentType;
        return this._openDialog(dialogConfig);
    }
    _openDialog(routeDialogConfig) {
        const dialogComponent = routeDialogConfig.component;
        const dialogConfig = routeDialogConfig.config || {};
        dialogConfig.viewContainerRef = this._viewContainerRef;
        // We dont want to close dialog when navigation happens,
        // because we want to have full control
        dialogConfig.closeOnNavigation = false;
        return this._dialogRouter.openDialogForRoute(dialogComponent, dialogConfig);
    }
}
FsDialogRouteComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogRouteComponent, deps: [{ token: i1.ActivatedRoute }, { token: i1.Router }, { token: FsDialogRouter }, { token: i0.ComponentFactoryResolver }, { token: i0.ViewContainerRef }], target: i0.ɵɵFactoryTarget.Component });
FsDialogRouteComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsDialogRouteComponent, selector: "ng-component", ngImport: i0, template: '<router-outlet></router-outlet>', isInline: true, directives: [{ type: i1.RouterOutlet, selector: "router-outlet", outputs: ["activate", "deactivate", "attach", "detach"], exportAs: ["outlet"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogRouteComponent, decorators: [{
            type: Component,
            args: [{
                    template: '<router-outlet></router-outlet>',
                }]
        }], ctorParameters: function () { return [{ type: i1.ActivatedRoute }, { type: i1.Router }, { type: FsDialogRouter }, { type: i0.ComponentFactoryResolver }, { type: i0.ViewContainerRef }]; } });

const FS_DAILOG_CONFIG = new InjectionToken('fs-dialog-config');

class FsDialogComponent {
    constructor(_dialogRef, _config, _breakpointObserver) {
        this._dialogRef = _dialogRef;
        this._config = _config;
        this._breakpointObserver = _breakpointObserver;
        this.mobileWidth = '600px';
        this.buttonLayout = 'flow';
        this._destroy$ = new Subject();
    }
    get classButtonLayoutFullWidth() {
        return this.buttonLayout === 'full';
    }
    ngOnChanges(changes) {
        if (changes.mode && !changes.mode.firstChange) {
            if (changes.mode.currentValue) {
                this.enableMode(this.mode);
            }
            else {
                this.disableMode();
            }
        }
    }
    ngOnInit() {
        this.mobileMode = this.mobileMode ?? (this._config?.mobileMode || 'full');
        this.mobileButtonPlacement = this.mobileButtonPlacement ?? (this._config?.mobileButtonPlacement || 'flow');
        if (this.mobileWidth) {
            this._breakpointObserver
                .observe([`(min-width: ${this.mobileWidth})`])
                .pipe(filter(() => !this.mode), takeUntil(this._destroy$))
                .subscribe((state) => {
                if (state.matches) {
                    this.disableMode();
                }
                else {
                    this.enableMode(this.mobileMode);
                }
            });
        }
        if (this.mode) {
            this.enableMode(this.mode);
        }
    }
    enableMode(mode) {
        mode = `fs-dialog-mode-${mode}`;
        this._dialogRef.addPanelClass(mode);
        this.backdropEl?.classList.add(mode);
        this.body.classList.add('fs-dialog-open', mode);
        this.mobileButtonPlacementClass = `button-placement-${this.mobileButtonPlacement}`;
    }
    disableMode() {
        this.mobileButtonPlacementClass = null;
        ['full', 'float', 'bottom']
            .forEach((mode) => {
            mode = 'fs-dialog-mode-' + mode;
            this._dialogRef.removePanelClass(mode);
            this.backdropEl?.classList.remove(mode);
            this.body.classList.remove('fs-dialog-open', mode);
        });
    }
    get backdropEl() {
        return document.querySelector('.cdk-overlay-dark-backdrop');
    }
    get body() {
        return document.body;
    }
    ngAfterContentInit() {
        this._dialogRef.addPanelClass('fs-dialog-overlay-pane');
        this.backdropEl?.classList.add('fs-dialog-overlay-backdrop');
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
        this.disableMode();
    }
}
FsDialogComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogComponent, deps: [{ token: i2.MatDialogRef, optional: true, skipSelf: true }, { token: FS_DAILOG_CONFIG, optional: true }, { token: i2$1.BreakpointObserver }], target: i0.ɵɵFactoryTarget.Component });
FsDialogComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsDialogComponent, selector: "fs-dialog", inputs: { mobileMode: "mobileMode", mobileButtonPlacement: "mobileButtonPlacement", mobileWidth: "mobileWidth", mode: "mode", buttonLayout: "buttonLayout" }, host: { properties: { "class": "this.mobileButtonPlacementClass", "class.button-layout-full": "this.classButtonLayoutFullWidth" } }, usesOnChanges: true, ngImport: i0, template: '<ng-content></ng-content>', isInline: true, styles: [""], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-dialog', template: '<ng-content></ng-content>', changeDetection: ChangeDetectionStrategy.OnPush, styles: [""] }]
        }], ctorParameters: function () { return [{ type: i2.MatDialogRef, decorators: [{
                    type: Optional
                }, {
                    type: SkipSelf
                }] }, { type: undefined, decorators: [{
                    type: Optional
                }, {
                    type: Inject,
                    args: [FS_DAILOG_CONFIG]
                }] }, { type: i2$1.BreakpointObserver }]; }, propDecorators: { mobileMode: [{
                type: Input
            }], mobileButtonPlacement: [{
                type: Input
            }], mobileWidth: [{
                type: Input
            }], mobileButtonPlacementClass: [{
                type: HostBinding,
                args: ['class']
            }], mode: [{
                type: Input
            }], buttonLayout: [{
                type: Input
            }], classButtonLayoutFullWidth: [{
                type: HostBinding,
                args: ['class.button-layout-full']
            }] } });

class FsDialogSubtitleDirective {
}
FsDialogSubtitleDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogSubtitleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FsDialogSubtitleDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "13.4.0", type: FsDialogSubtitleDirective, selector: "[fsDialogSubtitle]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogSubtitleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsDialogSubtitle]',
                }]
        }] });

class FsDialogTitleComponent {
    constructor() {
        this.close = false;
    }
}
FsDialogTitleComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogTitleComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
FsDialogTitleComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "13.4.0", type: FsDialogTitleComponent, selector: "fs-dialog-title", inputs: { close: "close" }, queries: [{ propertyName: "subtitle", first: true, predicate: FsDialogSubtitleDirective, descendants: true, read: TemplateRef }], ngImport: i0, template: "<div mat-dialog-title>\n  <div class=\"title\">\n    <ng-content></ng-content>\n    <ng-container *ngIf=\"subtitle\">\n      <div class=\"subtitle\">\n        <ng-container [ngTemplateOutlet]=\"subtitle\"></ng-container>\n      </div>\n    </ng-container>\n  </div>\n  <ng-container *ngIf=\"close\">\n    <div class=\"close\">\n      <a \n          mat-icon-button\n          mat-dialog-close>\n        <mat-icon>close</mat-icon>\n      </a>\n    </div>\n  </ng-container>\n</div>", styles: [".mat-dialog-title{display:flex;align-items:center}.mat-dialog-title .close{display:flex;flex:auto;justify-content:flex-end}.mat-dialog-title .subtitle{line-height:normal;font-size:60%}\n"], components: [{ type: i1$1.MatAnchor, selector: "a[mat-button], a[mat-raised-button], a[mat-icon-button], a[mat-fab],             a[mat-mini-fab], a[mat-stroked-button], a[mat-flat-button]", inputs: ["disabled", "disableRipple", "color", "tabIndex"], exportAs: ["matButton", "matAnchor"] }, { type: i2$2.MatIcon, selector: "mat-icon", inputs: ["color", "inline", "svgIcon", "fontSet", "fontIcon"], exportAs: ["matIcon"] }], directives: [{ type: i2.MatDialogTitle, selector: "[mat-dialog-title], [matDialogTitle]", inputs: ["id"], exportAs: ["matDialogTitle"] }, { type: i4.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { type: i4.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet"] }, { type: i2.MatDialogClose, selector: "[mat-dialog-close], [matDialogClose]", inputs: ["aria-label", "type", "mat-dialog-close", "matDialogClose"], exportAs: ["matDialogClose"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogTitleComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-dialog-title', changeDetection: ChangeDetectionStrategy.OnPush, template: "<div mat-dialog-title>\n  <div class=\"title\">\n    <ng-content></ng-content>\n    <ng-container *ngIf=\"subtitle\">\n      <div class=\"subtitle\">\n        <ng-container [ngTemplateOutlet]=\"subtitle\"></ng-container>\n      </div>\n    </ng-container>\n  </div>\n  <ng-container *ngIf=\"close\">\n    <div class=\"close\">\n      <a \n          mat-icon-button\n          mat-dialog-close>\n        <mat-icon>close</mat-icon>\n      </a>\n    </div>\n  </ng-container>\n</div>", styles: [".mat-dialog-title{display:flex;align-items:center}.mat-dialog-title .close{display:flex;flex:auto;justify-content:flex-end}.mat-dialog-title .subtitle{line-height:normal;font-size:60%}\n"] }]
        }], propDecorators: { subtitle: [{
                type: ContentChild,
                args: [FsDialogSubtitleDirective, { read: TemplateRef }]
            }], close: [{
                type: Input
            }] } });

class FsDialogModule {
    static forRoot() {
        return {
            ngModule: FsDialogModule,
            providers: [
                {
                    provide: MAT_DIALOG_DEFAULT_OPTIONS,
                    useValue: {
                        panelClass: 'fs-dialog-overlay-pane',
                        maxWidth: '95vw',
                        disableClose: false,
                        autoFocus: true,
                        hasBackdrop: true
                    }
                },
            ]
        };
    }
}
FsDialogModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsDialogModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogModule, declarations: [FsDialogComponent,
        FsDialogRouteComponent,
        FsDialogTitleComponent,
        FsDialogSubtitleDirective], imports: [CommonModule,
        RouterModule,
        MatIconModule,
        MatButtonModule,
        MatDialogModule], exports: [MatDialogModule,
        FsDialogComponent,
        FsDialogTitleComponent,
        FsDialogRouteComponent,
        FsDialogSubtitleDirective] });
FsDialogModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogModule, imports: [[
            CommonModule,
            RouterModule,
            MatIconModule,
            MatButtonModule,
            MatDialogModule,
        ], MatDialogModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialogModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        RouterModule,
                        MatIconModule,
                        MatButtonModule,
                        MatDialogModule,
                    ],
                    declarations: [
                        FsDialogComponent,
                        FsDialogRouteComponent,
                        FsDialogTitleComponent,
                        FsDialogSubtitleDirective,
                    ],
                    exports: [
                        MatDialogModule,
                        FsDialogComponent,
                        FsDialogTitleComponent,
                        FsDialogRouteComponent,
                        FsDialogSubtitleDirective,
                    ]
                }]
        }] });

class FsDialog {
    constructor(_dialogRouter) {
        this._dialogRouter = _dialogRouter;
    }
    open(component, config) {
        return this._dialogRouter.openDialog(component, config);
    }
    navigate(commands, extras) {
        return this._dialogRouter.navigate(commands, extras);
    }
    dialogRef$(component) {
        return this._dialogRouter.dialogRef$(component);
    }
}
FsDialog.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialog, deps: [{ token: FsDialogRouter }], target: i0.ɵɵFactoryTarget.Injectable });
FsDialog.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialog, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: FsDialog, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: FsDialogRouter }]; } });

function fsDialogRoute(route, dialogConfig) {
    const { path, component, data: routeData, ...restRoute } = route;
    const { data: dialogData } = dialogConfig || {};
    const data = {
        ...(routeData || {}),
        ...(dialogData || {}),
    };
    return {
        path: path,
        component: FsDialogRouteComponent,
        ...restRoute,
        data: {
            ...data,
            fsDialog: {
                component,
                config: {
                    data,
                    ...dialogConfig,
                },
            }
        }
    };
}

/**
 * Generated bundle index. Do not edit.
 */

export { FS_DAILOG_CONFIG, FsDialog, FsDialogComponent, FsDialogModule, FsDialogRouteComponent, FsDialogSubtitleDirective, FsDialogTitleComponent, fsDialogRoute };
